"use client";

import { ArrowRight } from "lucide-react";
import Image from "next/image";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { SectionHeading } from "../../home/section";
import { Stagger, StaggerItem } from "../../home/motion";

const FITT_LOGO = "/images/website/fitt-pilates.jpg";
const FITT_STUDIO = "/images/website/fitt-sudio.jpg";

/* ------------------------------------------------------------------ */
/* Shared: a faithful, small render of the real Fitt email            */
/* ------------------------------------------------------------------ */

type Mode = "light" | "dark" | "auto";

function MiniEmail({
  mode,
  imageHeight = 56,
  large = false,
}: {
  mode: Mode;
  imageHeight?: number;
  /** Bumps type + spacing for the frameless card, where the email fills a
   *  wider surface than the tiny phone screen. */
  large?: boolean;
}) {
  const dark = mode === "dark";
  const auto = mode === "auto";
  const bg = auto ? "bg-white dark:bg-[#0b0b0c]" : dark ? "bg-[#0b0b0c]" : "bg-white";
  const heading = auto ? "text-[#1f2937] dark:text-white" : dark ? "text-white" : "text-[#1f2937]";
  const body = auto
    ? "text-[#6b7280] dark:text-white/55"
    : dark
      ? "text-white/55"
      : "text-[#6b7280]";
  const pad = large ? "px-4" : "px-3";

  return (
    <div className={cn("font-sans", bg)}>
      <div className={cn(pad, large ? "pt-4" : "pt-3")}>
        <Image
          src={FITT_STUDIO}
          alt="Inside the new Fitt Pilates studio"
          width={1200}
          height={800}
          className="w-full rounded-[6px] object-cover"
          style={{ height: imageHeight }}
        />
      </div>
      <div className={cn(pad, large ? "pb-4 pt-3" : "pb-3.5 pt-2.5")}>
        <p className={cn("font-bold leading-tight", large ? "text-[13px]" : "text-[11px]", heading)}>
          We just opened on South Congress
        </p>
        <p
          className={cn(
            "leading-snug",
            large ? "mt-1.5 text-[11px]" : "mt-1 text-[9px]",
            body,
          )}
        >
          Free classes all weekend, plus founding-member pricing.
        </p>
        <div
          className={cn(
            "rounded-[6px] bg-[#007AFF] text-center font-semibold text-white",
            large ? "mt-3 py-2 text-[11px]" : "mt-2.5 py-[7px] text-[9px]",
          )}
        >
          Reserve your free class
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Shared: the email open in the iOS Mail client (no device frame)     */
/* ------------------------------------------------------------------ */

function MobileEmailCard({ mode = "auto" }: { mode?: Mode }) {
  const dark = mode === "dark";
  const auto = mode === "auto";
  const cardBg = auto ? "bg-white dark:bg-[#0b0b0c]" : dark ? "bg-[#0b0b0c]" : "bg-white";
  const border = auto ? "border-border" : dark ? "border-white/10" : "border-[#e5e7eb]";
  const name = auto ? "text-[#1f2937] dark:text-white" : dark ? "text-white" : "text-[#1f2937]";
  const muted = auto ? "text-[#9ca3af] dark:text-white/45" : dark ? "text-white/45" : "text-[#9ca3af]";
  const divider = auto ? "bg-[#e5e7eb] dark:bg-white/10" : dark ? "bg-white/10" : "bg-[#e5e7eb]";

  return (
    <div
      className={cn(
        "w-[250px] overflow-hidden rounded-[1.4rem] border shadow-xl shadow-black/10 dark:shadow-black/40",
        cardBg,
        border,
      )}
    >
      {/* iOS Mail message header: sender avatar, name, time, recipient */}
      <div className="flex items-center gap-2.5 px-4 py-3">
        <Image
          src={FITT_LOGO}
          alt="Fitt Pilates"
          width={96}
          height={96}
          className="size-8 shrink-0 rounded-full object-cover"
        />
        <div className="min-w-0 flex-1 leading-tight">
          <div className="flex items-center justify-between gap-2">
            <span className={cn("truncate text-[12px] font-semibold", name)}>Fitt Pilates</span>
            <span className={cn("shrink-0 text-[10px]", muted)}>8:24 AM</span>
          </div>
          <span className={cn("mt-0.5 block text-[10px]", muted)}>to me</span>
        </div>
      </div>
      <div className={cn("h-px", divider)} />
      <MiniEmail mode={mode} imageHeight={82} large />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 2 · Dark-mode — the same email card, light and dark side by side    */
/* ------------------------------------------------------------------ */

function DarkModeMock() {
  // Reuses the box-1 email card so the layout is identical; scaled down as a
  // pair so a light + dark copy fit side by side.
  return (
    <div className="scale-[0.62]">
      <div className="flex items-center">
        <div className="-mr-20 rotate-[-5deg]">
          <MobileEmailCard mode="light" />
        </div>
        <div className="relative z-10 rotate-[5deg]">
          <MobileEmailCard mode="dark" />
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 3 · Personalization — merge fields resolved per recipient          */
/* ------------------------------------------------------------------ */

function MergeRow({ token, value }: { token: string; value: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="rounded-md border border-primary/30 bg-primary/5 px-1.5 py-1 font-mono text-[10px] font-medium text-primary dark:border-brand-accent/30 dark:bg-brand-accent/10 dark:text-brand-accent">
        {token}
      </span>
      <ArrowRight className="size-3 shrink-0 text-muted-foreground" />
      <span className="truncate text-[12px] font-semibold text-foreground">{value}</span>
    </div>
  );
}

function PersonalizationMock() {
  return (
    <div className="w-full max-w-[248px] overflow-hidden rounded-xl border border-border bg-background shadow-xl shadow-black/5 dark:shadow-black/20">
      <div className="border-b border-border px-3.5 py-3">
        <p className="truncate text-[12px] font-semibold text-foreground">
          Hi <span className="text-primary dark:text-brand-accent">Maya</span>, your spot is saved
        </p>
      </div>
      <div className="space-y-2.5 p-3.5">
        <MergeRow token="{first_name}" value="Maya" />
        <MergeRow token="{gym.name}" value="Fitt Pilates" />
        <MergeRow token="{next_class}" value="Sat · 9:00 AM" />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Section                                                            */
/* ------------------------------------------------------------------ */

type Highlight = { visual: ReactNode; title: string; body: string };

const HIGHLIGHTS: Highlight[] = [
  {
    visual: (
      <div className="scale-90">
        <MobileEmailCard />
      </div>
    ),
    title: "Built for the phone first",
    body: "Most email is opened on a phone, so Helios designs for the small screen first.",
  },
  {
    visual: <DarkModeMock />,
    title: "Flawless in dark mode",
    body: "A growing share of inboxes render dark. Helios ships a clean dark variant of every email automatically — no white boxes, no invisible text.",
  },
  {
    visual: <PersonalizationMock />,
    title: "Personal to every member",
    body: "Drop in merge fields and Helios fills them per recipient at send time, pulling from the same customer data the rest of the platform runs on.",
  },
];

export function EmailBuilderHighlights() {
  return (
    <section className="bg-background pt-20 lg:pt-28">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <SectionHeading
          align="left"
          size="sm"
          title={
            <>
              Good-looking by default, everywhere it lands.
              <span className="text-muted-foreground">
                {" "}
                Mobile-first, dark-mode ready, and personal to every member — without touching a
                line of code.
              </span>
            </>
          }
        />
      </div>

      <div className="mt-12 border-t border-border bg-grid lg:mt-16">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <Stagger className="grid grid-cols-1 divide-y divide-border border-x border-border bg-background lg:grid-cols-3 lg:divide-x lg:divide-y-0">
            {HIGHLIGHTS.map((h) => (
              <StaggerItem key={h.title} className="flex flex-col px-6 py-8 sm:px-8 sm:py-10">
                <div className="flex h-[300px] items-center justify-center">{h.visual}</div>
                <div className="mt-7">
                  <h3 className="text-base font-bold tracking-tight text-foreground">{h.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{h.body}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </div>
    </section>
  );
}
